import {
  MessageList,
  MessageInput,
  Window,
  useChannelActionContext,
  Thread,
  useChatContext,
  StreamMessage,
  DefaultStreamChatGenerics,
  MessageToSend

} from 'stream-chat-react';

import { MessagingChannelHeader } from '../../components';
import { useGiphyContext } from '../../context';
import "./ChannelInner.css"
import { useCallback, useEffect, useState } from 'react';
import HybridWebView from '../../hybridWebView/HybridWebView';
import Interop from '../../interfaces/Interop';

import CustomMessageRenderer from '../../CustomMessageList/CustomMessageRenderer';
import { ChatPageNavigation } from '../../models/ChatPageNavigation';
import { useAlertContext } from '../../context/AlertContext';  // Import the custom hook
import CustomAlert from '../CustomAlert/CustomAlert';
import { useBlockUnblockContext } from '../../context/BlockUnblock';
import { ClientCredential } from '../../models/ClientCredential';
import {  StreamChat } from 'stream-chat';
import { ChannelDataModel } from '../../models/ChannelDataModel';
import { useChannelContext } from '../ChannelContext/ChannelContext';

// Functions to manage dynamic textarea height
function getTextArea() {
  const textArea = document.querySelector("textarea.rta__textarea.str-chat__textarea__textarea.str-chat__message-textarea") as HTMLElement;
  return textArea
}


/**
 * HELPER: onTextareaInputKey
 * Dynamically adjusts textarea height as user types
 * Expands up to max height, then enables scroll
 */
function onTextareaInputKey(this: HTMLElement) {
  const defaultHeight = getTextAreaDefaultHeight(this);
  const maxHeight = parseInt(getTextAreaMaxDefaultHeight(this));

  this.style.height = defaultHeight + "px";
  if (this.scrollHeight < maxHeight) {
    this.style.overflowY = 'hidden';
    this.style.height = this.scrollHeight + "px";
  }
  else {
    this.style.overflowY = 'auto';
    this.style.height = maxHeight + 'px';
  }
}

/**
 * HELPER: makeTextAreaHeightDynamic
 * Initializes textarea with dynamic height based on content
 */
function makeTextAreaHeightDynamic(textArea: HTMLElement) {
  textArea.style.removeProperty('height');
  textArea.style.height = textArea.scrollHeight + "px";
  textArea.style.overflowY = 'hidden';
}

/**
 * HELPER: getTextAreaDefaultHeight
 * Gets default height from CSS variable
 */
function getTextAreaDefaultHeight(textArea: HTMLElement) {
  const computedStyle = getComputedStyle(textArea);
  const value = computedStyle.getPropertyValue("--str-chat__textarea-height");
  return value;
}

/**
 * HELPER: getTextAreaMaxDefaultHeight
 * Gets max height from CSS variable
 */
function getTextAreaMaxDefaultHeight(textArea: HTMLElement) {
  const computedStyle = getComputedStyle(textArea);
  const value = computedStyle.getPropertyValue("--str-chat__textarea-max-height");
  return value;
}

/**
 * HELPER: resetTextAreaHeight
 * Resets textarea to default height after message sent
 */
function resetTextAreaHeight(textArea: HTMLElement) {
  const defaultHeight = getTextAreaDefaultHeight(textArea);
  const height = parseInt(defaultHeight) + 2;
  textArea.style.height = height + 'px';
  textArea.style.overflowY = 'hidden';
}

/**
 * HELPER: shouldSubmit
 * Determines if Enter key should send message
 * Desktop: Enter sends, Shift+Enter for new line
 * Mobile WebView: Different behavior
 */
function shouldSubmit(event: KeyboardEvent) {
  if (HybridWebView.IsWebView() === false) {
    return event.key === 'Enter' && !event.shiftKey;
  }
  return HybridWebView.IsWebView() === false && event.key === 'Enter';
}

/**
 * HELPER: addKeyboardObserver
 * Monitors iOS keyboard open/close to auto-scroll messages
 * When keyboard opens, scrolls to latest message
 */
function addKeyboardObserver() {

  if (window.visualViewport !== null) {
    window.visualViewport.onresize = _ => {

      const MINIMUM_KEYBOARD_HEIGHT = 300;

      if (window.visualViewport && ((window.visualViewport.height + MINIMUM_KEYBOARD_HEIGHT) < window.screen.height)) {
        // On keyboard open.
        const listNode = document.querySelector("div.str-chat__list") as HTMLElement;

        const ulNode = document.querySelector("ul.str-chat__ul") as HTMLElement;

        if (ulNode.scrollHeight > listNode.clientHeight) {
          const allListElements = document.querySelectorAll("li.str-chat__li");

          if (allListElements.length != 0) {
            //value = `${listNode.scrollHeight - listNode.clientHeight - 8} < ${listNode.scrollTop}`;

            const lastListElement = allListElements[allListElements.length - 1];
            lastListElement.scrollIntoView({ behavior: "smooth" });
          }
        }
      }
      else {
        // On keyboard close.
      }
    }
  }
}

/**
 * HELPER: removeKeyboardObserver
 * Cleans up keyboard resize listener
 */
function removeKeyboardObserver() {
  if (window.visualViewport !== null) {
    window.visualViewport.onresize = null;
  }
}

/**
 * HELPER: scrollToLatestMessage
 * Scrolls message list to bottom (latest message)
 * Retries if DOM elements not ready yet (max 5 attempts)
 * Uses multiple scroll methods for iOS compatibility
 */
function scrollToLatestMessage(attempt: number = 0, maxAttempts: number = 5) {
  const listNode = document.querySelector("div.str-chat__list") as HTMLElement;
  const ulNode = document.querySelector("ul.str-chat__ul") as HTMLElement;
  
  if (!listNode || !ulNode) {
    // Retry if elements not found yet
    if (attempt < maxAttempts) {
      setTimeout(() => scrollToLatestMessage(attempt + 1, maxAttempts), 200);
    }
    return;
  }

  const allListElements = document.querySelectorAll("li.str-chat__li");
  
  if (allListElements.length === 0) {
    // No messages yet, retry
    if (attempt < maxAttempts) {
      setTimeout(() => scrollToLatestMessage(attempt + 1, maxAttempts), 200);
    }
    return;
  }

  // Try multiple scroll methods for iOS compatibility
  const lastListElement = allListElements[allListElements.length - 1];
  
  // Method 1: Scroll the container directly (more reliable for iOS)
  if (listNode.scrollHeight > listNode.clientHeight) {
    listNode.scrollTop = listNode.scrollHeight;
  }
  
  // Method 2: scrollIntoView as fallback
  setTimeout(() => {
    lastListElement.scrollIntoView({ behavior: "smooth", block: "end" });
  }, 50);
  
  // Method 3: Force scroll again after a delay (iOS sometimes restores scroll)
  setTimeout(() => {
    if (listNode.scrollTop < (listNode.scrollHeight - listNode.clientHeight - 50)) {
      listNode.scrollTop = listNode.scrollHeight;
    }
  }, 300);
}

/**
 * HELPER: showHideShareMediaIcon
 * Shows/hides attachment button based on block status
 */
function showHideShareMediaIcon(value: boolean) {
  const shareMediaIcon = document.querySelector("div.str-chat__file-input-container") as HTMLElement;
  if (shareMediaIcon != null) {
    if (value) {
      shareMediaIcon.style.display = "none";
    }
    else {
      shareMediaIcon.style.display = "flex ";
    }
  }
}

/**
 * HELPER: showEmojiIcon
 * Shows/hides emoji picker button
 * Only affects desktop (mobile uses native keyboard)
 */
function showEmojiIcon(platformType:string,value: boolean) {
  const EmojiPickerIcon = document.querySelector("div.str-chat__message-textarea-emoji-picker") as HTMLElement;
  if(platformType!=="Mobile"){
    if (EmojiPickerIcon != null) {
      if (value) {
        EmojiPickerIcon.style.display = "none";
      }
      else {
        EmojiPickerIcon.style.display = "flex ";
      }
    }
  }else{
  }
  
}


export type ChannelInnerProps = {
  toggleMobile: () => void;
  interop: Interop;
  theme: string;
  platformType: string;
  languageCode: any;
  pageType: ChatPageNavigation;
};

const ChannelInner = (props: ChannelInnerProps) => {
  const { theme, interop, platformType, languageCode, toggleMobile, pageType } = props;
  const { giphyState, setGiphyState } = useGiphyContext();
  var { setActiveChannel, channel, client } = useChatContext<DefaultStreamChatGenerics>();
  const { sendMessage, markRead } = useChannelActionContext<DefaultStreamChatGenerics>();
  const [isBlocked, setBlocked] = useState<boolean>(false);
  const [isMuted, setMuted] = useState<boolean>(false);
  const [isBlockIconVisible, setHideBlockIcon] = useState(false);
  const actions = ['delete', 'edit', 'react', 'reply'];
  const ThreadActions = ['delete', 'edit', 'react'];
  const [chatClient, setChatClient] = useState<any>(null);
 
  var pushMessageId: any = pageType.PushMessageId;
  const { showAlert, alertMessage, setShowAlert, setAlertMessage } = useAlertContext();

  const { isUserBlocked, toggleBlock, blockedUsers } = useBlockUnblockContext(); // Use context to get blocked status
  const [blockedChannels, setBlockedChannels] = useState<{ [key: string]: boolean }>({});
  const [refreshToken, setRefreshToken] = useState<ClientCredential>();

  const { selectedChannel, setSelectedChannel } = useChannelContext(); // Get context
  const [totalUnreadCount, setTotalUnreadCount] = useState<number | null>(null);
  const apiKey = process.env.REACT_APP_STREAM_KEY || "";
  
  // calculate channel members count to set mention limit appropriately
  const channelMembersCount = channel?.data?.member_count ;

  useEffect(() => {
    // Check if we're navigating back (set by the back button in MessagingChannelHeader)
    // If so, skip setting the active channel to prevent overriding the back navigation
    const isNavigatingBack = (window as any).__isNavigatingBack === true;
    
    if (!isNavigatingBack) {
      // Only set active channel if we're not navigating back
      // This prevents the back button action from being overridden
      setActiveChannel(channel)
    }
  }, []);

  /**
   * EFFECT: Reconnect on token refresh
   * Creates new client and reconnects when refreshToken updates
   */
  useEffect(() => {
    if (refreshToken) {

    //  console.log(refreshToken);
      const client = new StreamChat<DefaultStreamChatGenerics>(apiKey, {
        enableInsights: true,
        enableWSFallback: true,
      });

      const connectUser = async () => {
        await client?.connectUser({ id: refreshToken.UserId, eventId: refreshToken.EventId }, refreshToken.UserToken)
          .then((data) => {
            setChatClient(client);

            const channeldata = client.channel(channel!.type, channel!.id);
            // Get the channel and set it in state
            setActiveChannel(channeldata);
      //      console.log('Token refreshed!' + data);
          })
          .catch((error: any) => {
      //      console.log('Failed to refresh token' + error);
          });
      };
      connectUser();

    }
  }, [refreshToken]);


 /**
 * FUNCTION: overrideSubmitHandler
 * 
 * Custom message submission handler that overrides Stream Chat's default
 * Handles Giphy commands, parent messages, token refresh, and UI reset
 * 
 * EXECUTION FLOW:
 * 1. Check if message has attachments and /giphy command - strip command
 * 2. Check if giphy picker is active - add /giphy prefix
 * 3. Prepare message object with parent reference and mentioned users
 * 4. Send message via Stream Chat API
 * 5. Mark channel as read, reset textarea height, refocus input
 * 6. If auth error (code 40), request new credentials and reconnect
 * 
 * PARAMETERS:
 * - message: The message object from MessageInput component
 * 
 * RETURNS:
 * Promise<void>
 */
  const overrideSubmitHandler = async (message: MessageToSend<any>) => {
    let updatedMessage;

    try {

      if (message.attachments?.length && message.text?.startsWith('/giphy')) {
        const updatedText = message.text.replace('/giphy', '');
        updatedMessage = { ...message, text: updatedText };
      }

      if (giphyState) {
        const updatedText = `/giphy ${message.text}`;
        updatedMessage = { ...message, text: updatedText };
      }

      const newMessage = updatedMessage || message;
      const parentMessage = newMessage.parent;

      const messageToSend = {
        ...newMessage,
        parent: parentMessage
          ? {
            ...parentMessage,
            created_at: parentMessage.created_at?.toString(),
            pinned_at: parentMessage.pinned_at?.toString(),
            updated_at: parentMessage.updated_at?.toString(),
          }
          : undefined,
        mentioned_users: newMessage.mentioned_users?.map(user => user.id),  // Ensure this is an array of strings
      };

      const response = await channel?.sendMessage(messageToSend);

      markRead();
      const textArea = getTextArea();
      resetTextAreaHeight(textArea);
      textArea.focus();
      setGiphyState(false);

    } catch (error: any) {
      // Handle errors such as network issues or API errors
      if (error.code == 40) {
        try {
          await interop.GetCredentials().then(async credentialData => {
            setRefreshToken(credentialData);
          });
        } catch (refreshError) {
          console.error('Error during refresh token process:', refreshError);
        }
      } else {
        console.error('Error', error);

      }
    }
  };
 
  const members = Object.values(channel!.state.members || {}).filter(
    ({ user }) => user?.id !== client?.userID,
  );



  useEffect(() => {
    // Auto-hide the alert after 3 seconds
    const timer = showAlert
      ? setTimeout(() => {
        setShowAlert(false); // Hide the alert after 3 seconds
      }, 2000)
      : null;

    // Cleanup function
    return () => {
      if (timer) {
        clearTimeout(timer); // Clean up the timeout if it was set
      }
    };
  }, [showAlert, setShowAlert]);

  useEffect(() => {
    // Disable iOS scroll restoration to prevent it from overriding our scroll
    if (typeof window !== 'undefined' && 'history' in window && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

    /**
   * EFFECT: Handle push notification deep linking and channel setup
   * Opens thread if push is for reply, auto-joins group channels, sets mute status
   */
  useEffect(() => {

    if (channel != null && client != null) {
      if (pushMessageId && typeof pushMessageId === 'string') {        //open thread directly
        client.getMessage(pushMessageId).then((data1: any) => {
          const replyCount = data1.message.reply_count
          var fetchedMessage = data1.message as any;
          if (replyCount > 0) {
            pageType.PushMessageId = "";
         //   console.log(pushMessageId);
            openThread(fetchedMessage);
          } else {
            // Handle regular message (non-thread) - scroll will be handled by separate useEffect
            pageType.PushMessageId = "";
          }
        }).catch((error) => {
          console.error('Error fetching push message:', error);
        });
      }
      var isAlreadyMember = false;
      if (channel.type == "public_with_membership") {

        if (members.length != 0) {
          for (let i = 0; i < members.length; i++) {
            if (members[i].user_id === client?.userID) {
              isAlreadyMember = true;
              break;
            }
          }
        }
        if (!isAlreadyMember) {
          channel.addMembers([{ user_id: client?.user?.id!!, channel_role: "channel_member" }])
        }
        
        // Initialize share media icon visibility for public channels with membership
        // For public channels with membership, the icon should be visible by default
        // Use setTimeout to ensure DOM elements are available
        setTimeout(() => {
          showHideShareMediaIcon(false);
          showEmojiIcon(platformType, false);
        }, 100);
      }

      var mutedStatus = channel.muteStatus();
      setMuted(mutedStatus.muted);
      interop.PushHandle(channel.cid);
    }
  }, []);

  /**
   * EFFECT: iOS push notification scroll handling
   * Waits for messages to load and DOM to render before scrolling
   * Only runs on iOS WebView for non-thread push notifications
   */
  useEffect(() => {
    // Only handle if this is a push notification (non-thread) on iOS
    if (!pushMessageId || typeof pushMessageId !== 'string') return;
    if (!channel || !client) return;
    
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipod|ipad/.test(userAgent);
    const isWebView = HybridWebView.IsWebView();
    
    if (!isIOS || !isWebView) return; // Android/PWA will auto-scroll
    
    // Check if channel has messages loaded
    const messageCount = channel.state.messages?.length || 0;
    if (messageCount === 0) {
      // Messages not loaded yet, wait for channel state to update
      // This will re-trigger when messages are loaded
      return;
    }
    
    // Messages are in state, now wait for DOM to render
    // Use requestAnimationFrame for better timing
    const scrollWhenReady = () => {
      requestAnimationFrame(() => {
        const listNode = document.querySelector("div.str-chat__list") as HTMLElement;
        const messages = document.querySelectorAll("li.str-chat__li");
        
        if (listNode && messages.length > 0) {
          // DOM is ready, scroll now
          scrollToLatestMessage();
        } else {
          // DOM not ready yet, try again
          setTimeout(scrollWhenReady, 100);
        }
      });
    };
    
    // Start checking after a small delay to let React render
    setTimeout(scrollWhenReady, 200);
  }, [channel?.state.messages?.length, pushMessageId, channel?.id, client?.userID]);

  /**
   * EFFECT: Auto-mark messages as read when channel opens
   * Checks for unread messages and marks them read
   */
  useEffect(() => {
    // Check if channel and client are available
    if (channel != null && client != null) {
      // Get the current user's read state for this channel
      const readState = channel.state.read[client.userID!];
      
      // Check if there are unread messages
      const unreadCount = readState?.unread_messages || 0;
      
      // If there are unread messages, mark them as read
      if (unreadCount > 0) {
        // Call markRead to mark all messages in the channel as read
        markRead();
        
        // Log the read status for debugging (optional)
        // console.log(`Channel ${channel.id} opened with ${unreadCount} unread messages. Marking as read.`);
      } else {
        // All messages are already read
        // console.log(`Channel ${channel.id} opened - all messages are already read.`);
      }
    }
  }, [channel?.id, client?.userID, markRead]);

  useEffect(() => {
    channel!.on('all', (event: any) => {
      if (event.type === 'all') {
        let value = false;
        if (event.text === "user blocked") {
          // Directly manipulate the DOM to show the channel listing
          // This is what the useMobileView hook does when isMobileNavVisible is true
          const mobileChannelList = document.querySelector('#mobile-channel-list');
          if (mobileChannelList) {
            mobileChannelList.classList.add('show');
            document.body.style.overflow = 'hidden';
          }
          // Clear the active channel first
          setSelectedChannel(null);   
          if (event.user.id === members[0].user_id!!) {
            setHideBlockIcon(true)
          }
          value = true
        } else {
          setHideBlockIcon(false)
        }
        setBlocked(value);
        showHideShareMediaIcon(value)
        showEmojiIcon(platformType,value)
      }
      if (event.type === "message.new") {
          if (event.channel_type === "public_without_membership") {
            const details = selectedChannel?.data?.details || '';  // Default to empty string
            let EntityId = '';

            if (details !== "") {
              try {
                const parsedObject = JSON.parse(details as string);
                EntityId = parsedObject.EntityId || '';  // Default to empty string if not present
              } catch (error) {
                console.error("Failed to parse details:", error);
                EntityId = '';  // If parsing fails, set EntityId to empty string
              }
            }

            const channelData = new ChannelDataModel(
              event?.created_at,
              event.channel_type,
              false,
              event.cid,
              EntityId.toString() || '',  // Ensure EntityId is a string
              event.user.id,
            );
            interop.SendNewMessageUpdate(channelData);
          }
      }
      if (event.type === "message.read") {

        if (event.user?.id === client?.user?.id) {
          if (selectedChannel !== undefined || selectedChannel != null) {
            var isActiveChannel = selectedChannel?.id === event.channel_id;

            SetUnreadCountStatus().then(val => {
              setTotalUnreadCount(val);
              if (val !== null) {
                if (val == selectedChannel?.state.unreadCount) {
                  isActiveChannel = true;
                }
                else {
                  isActiveChannel = false;
                }

                const details = selectedChannel.data?.details || '';  // Default to empty string
                let EntityId = '';
                
                if (details !== "") {
                  try {
                    const parsedObject = JSON.parse(details as string);
                    EntityId = parsedObject.EntityId || '';  // Default to empty string if not present
                  } catch (error) {
                    console.error("Failed to parse details:", error);
                    EntityId = '';  // If parsing fails, set EntityId to empty string
                  }
                }
                
                const channelData = new ChannelDataModel(
                  event?.received_at,
                  event.channel_type,
                  isActiveChannel,
                  event.cid,
                  EntityId.toString() || '',  // Ensure EntityId is a string
                event.user.id);
                  SendNewMessageUpdate(interop, channelData);
              }
            });
          }
        }
      }
    });
  }, []);

  /**
   * HELPER: SendNewMessageUpdate
   * Sends message update to native platform
   */
  async function SendNewMessageUpdate(interop: Interop, data: ChannelDataModel) {
    interop.SendNewMessageUpdate(data);
  }

  /**
   * HELPER: SetUnreadCountStatus
   * Gets total unread count across all channels
   */
  async function SetUnreadCountStatus() {
    var totalUnreadCount = await getUnreadCount(client!, client?.userID!!);
    return totalUnreadCount;

  }
    
  /**
   * HELPER: getUnreadCount
   * Fetches unread count from Stream API
   */
  async function getUnreadCount(
    chatClient: StreamChat<DefaultStreamChatGenerics>,
    userId: string
  ) {

    const response = await chatClient.getUnreadCount(userId);
    return response.total_unread_count;
  }
  /**
   * EFFECT: Initialize block state for DM channels
   * Sets initial blocked state and icon visibility
   */
  useEffect(() => {
    if (channel!.type === 'messaging') {

      var blockedState = false;

      if (channel!.data?.frozen !== undefined && channel!.data?.frozen == true) {
        blockedState = true
      }
      setBlocked(blockedState)
      showHideShareMediaIcon(blockedState)
      showEmojiIcon(platformType,blockedState)

      if (client?.userID === members[0].user_id!!)
        setHideBlockIcon(true)
      else
        setHideBlockIcon(false)
    }
  }, [isUserBlocked, blockedUsers]);

  /**
   * EFFECT: Update block state when blockedChannels changes
   */
  useEffect(() => {

    if (channel !== null && channel !== undefined) {
      setBlocked(blockedChannels[channel!.id!!] ?? false); // Ensure the state is updated whenever blockedChannels changes

    }
  }, [blockedChannels]);

  /**
   * Expose PerformBackAction to global scope for native platform
   */
  (window as any).PerformBackAction = () => {
    toggleMobile();
  };
  
   /**
   * CALLBACK: UpdateMutedUnMutedState
   * Updates mute state from header action
   */
  const UpdateMutedUnMutedState = (value: boolean) => {
    setMuted(value);
  }

   /**
   * CALLBACK: UpdateBlockUnblockState
   * Updates block state and UI from header action
   */
  const UpdateBlockUnblockState = (value: boolean) => {
    setBlocked(value);
    showHideShareMediaIcon(value)
    showEmojiIcon(platformType,value)
  }

  /**
   * EFFECT: Setup textarea dynamic height and keyboard observer
   */
  useEffect(() => {
    addKeyboardObserver();
    const textArea = getTextArea();
    if (textArea) {
      makeTextAreaHeightDynamic(textArea);
      textArea.addEventListener('input', onTextareaInputKey, false);
    }

    return () => {
      if (textArea) {
        removeKeyboardObserver();
        textArea.removeEventListener('input', onTextareaInputKey);
      }
    };
  }, []);



  const { openThread: contextOpenThread } = useChannelActionContext<DefaultStreamChatGenerics>();

    /**
   * FUNCTION: openThread
   * Opens thread view for a message
   */
  const openThread = useCallback(
    (message: StreamMessage<DefaultStreamChatGenerics>, event?: React.BaseSyntheticEvent) => {
      contextOpenThread(message, event);
    },
    [contextOpenThread],
  );

  return (
    <>
      <Window>

        <MessagingChannelHeader theme={theme} toggleMobile={toggleMobile} isBlocked={isBlocked} isMuted={isMuted} interop={interop}
          isBlockIconVisible={isBlockIconVisible}
          languageCode={languageCode}
          UpdateBlockUnblockState={UpdateBlockUnblockState} UpdateMutedUnMutedState={UpdateMutedUnMutedState} platformType={platformType} />
        {<MessageList messageActions={actions} closeReactionSelectorOnClick={true} Message={CustomMessageRenderer} onlySenderCanEdit={true}
          openThread={openThread} hideNewMessageSeparator={true}/>}
        {showAlert && <CustomAlert message={alertMessage} />}

        <MessageInput 
         // pass the members count to avoid being overriden by stream's default value of 7
         mentionQueryParams={{  
            options: { limit: channelMembersCount!-1 } // exclude me : otherwise removed users are shown in mention list
          }}
          disableMentions={(channel !== null && channel!.type === "public_with_membership") ? false : true}
          shouldSubmit={shouldSubmit}
          overrideSubmitHandler={overrideSubmitHandler}
          disabled={isBlocked}
          hideSendButton={isBlocked}
          additionalTextareaProps={{ onPaste: undefined, placeholder: isBlocked ? languageCode['Blocked'] : languageCode['Type your message'] }} />
      </Window>
      <Thread
        additionalMessageInputProps={{ disableMentions: (channel !== null && channel!.type === "public_with_membership") ? false : true }}
        Message={CustomMessageRenderer} messageActions={ThreadActions} />

    </>
  );
};

export default ChannelInner;
